import React, { useState, useEffect, useMemo, useCallback } from "react";
import { X, Plus, Info, Pencil, Trash2, Loader } from "lucide-react";
import SearchableSelect from "./SearchableSelect";
import { masterService } from "../api/masterService";
import conversionService from "../api/conversionService";
import { useNotification } from "../contexts/NotificationContext";
import {
  formatNumberWithSeparator,
  parseNumberFromSeparator,
} from "../utils/numberFormat";

// Modal khusus "Tambah Satuan Jual" — flow konversi stok dipindahkan dari
// modal varian merek lama. Tidak ada pemilihan merek di sini. Setiap baris
// = satu (satuan jual + nilai konversi + harga jual).
// Saat disimpan:
// 1. Buat conversion rule (from_unit_id → product base unit, factor)
// 2. Untuk setiap merek yang sudah ada, buat varian (brand × unit × harga)
//    Jika belum ada merek, hanya conversion rule yang dibuat.
export default function AddSellingUnitsModal({
  isOpen,
  onClose,
  productId,
  product,
  existingVariants = [],
  existingConversionRules = [],
  editingUnitId = null,  // when set → edit mode: auto-open sub-modal for this unit
  onSuccess,
}) {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [conversionRows, setConversionRows] = useState([]);
  
  // Local state for product selection/loading
  const [selectedProductId, setSelectedProductId] = useState(productId || "");
  const [selectedProduct, setSelectedProduct] = useState(product || null);
  const [localVariants, setLocalVariants] = useState(existingVariants || []);
  const [localRules, setLocalRules] = useState(existingConversionRules || []);
  const [loadingProductData, setLoadingProductData] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedProductId(productId || "");
      setSelectedProduct(product || null);
      setLocalVariants(existingVariants || []);
      setLocalRules(existingConversionRules || []);
      setConversionRows([]);
    }
  }, [isOpen, productId, product, existingVariants, existingConversionRules]);

  // Fetch product context when product ID changes (only if selected inside the modal)
  useEffect(() => {
    if (!selectedProductId || productId) return;
    
    setLoadingProductData(true);
    Promise.all([
      masterService.products.get(selectedProductId),
      masterService.products.variants.list(selectedProductId),
      conversionService.rules.list({ product_id: selectedProductId })
    ])
      .then(([prodRes, varRes, rulesRes]) => {
        const prod = prodRes.data || prodRes;
        setSelectedProduct(prod);
        
        const varData = varRes.data || varRes;
        setLocalVariants(
          Array.isArray(varData) ? varData : (varData.items || varData.data || [])
        );
        
        const rulesData = rulesRes.data || rulesRes;
        setLocalRules(
          Array.isArray(rulesData) ? rulesData : (rulesData.items || rulesData.data || [])
        );
      })
      .catch((err) => {
        console.error("Failed to fetch product details:", err);
      })
      .finally(() => {
        setLoadingProductData(false);
      });
  }, [selectedProductId, productId]);

  const existingRuleUnits = useMemo(() => {
    return new Set(localRules.map((r) => r.from_unit_id));
  }, [localRules]);

  const [subModalOpen, setSubModalOpen] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState(null);

  const baseUnitId = selectedProduct?.unit_id;
  const baseUnitName = selectedProduct?.unit_name || "Liter";

  // Harga jual dasar
  const baseVariant = localVariants.find((v) => v.unit_id === baseUnitId);
  const hargaJualDasar = (() => {
    if (baseVariant?.sell_price) return Number(baseVariant.sell_price);
    const prices = localVariants
      .map((v) => Number(v.sell_price || 0))
      .filter((p) => p > 0);
    return prices.length > 0 ? Math.min(...prices) : (Number(selectedProduct?.sell_price) || 0);
  })();

  // Load data konversi eksisting ketika modal dibuka
  useEffect(() => {
    if (!isOpen) return;
    
    if (localRules && localRules.length > 0) {
      const grouped = {};
      localRules.forEach((rule) => {
        const unitId = rule.from_unit_id;
        if (!grouped[unitId]) {
          grouped[unitId] = {
            unit_id: unitId,
            unit_name: rule.from_unit_name || rule.from_unit?.name || "",
            conversion_factor: rule.conversion_factor,
            sell_price: rule.sell_price || 0,
            barcode: "",
            variant_prices: {},
            variant_barcodes: {},
            ruleIds: {},  // { [variant_id | '__base__']: rule_id }
          };
        }
        if (rule.variant_id) {
          grouped[unitId].variant_prices[rule.variant_id] = rule.sell_price;
          grouped[unitId].variant_barcodes[rule.variant_id] = rule.barcode || "";
          grouped[unitId].ruleIds[rule.variant_id] = rule.id;
        } else {
          grouped[unitId].barcode = rule.barcode || "";
          grouped[unitId].ruleIds['__base__'] = rule.id;
        }
      });
      const initialRows = Object.values(grouped);
      setConversionRows(initialRows);
    } else {
      setConversionRows([]);
    }
  }, [isOpen, localRules, localVariants]);

  const handleAddRow = () => {
    setEditingRowIndex(null);
    setSubModalOpen(true);
  };

  const handleEditRow = (index) => {
    setEditingRowIndex(index);
    setSubModalOpen(true);
  };

  const handleDeleteRow = (index) => {
    setConversionRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubModalSave = (row) => {
    if (editingRowIndex !== null) {
      setConversionRows((prev) => {
        const next = [...prev];
        next[editingRowIndex] = { ...next[editingRowIndex], ...row };
        return next;
      });
    } else {
      setConversionRows((prev) => [...prev, row]);
    }
    setSubModalOpen(false);
    setEditingRowIndex(null);
  };

  const handleSubmit = async () => {
    if (!baseUnitId) {
      showError("Produk ini belum memiliki Satuan Dasar. Silakan Edit Produk terlebih dahulu.");
      return;
    }
    if (conversionRows.length === 0) {
      showError("Tambahkan minimal satu baris konversi stok");
      return;
    }

    setLoading(true);
    try {
      const existingRuleUnitIds = new Set(localRules.map((r) => r.from_unit_id));

      const ruleOps = [];
      const failedMessages = [];
      conversionRows
        .filter((row) => row.unit_id && row.unit_id !== baseUnitId && Number(row.conversion_factor) > 0)
        .forEach((row) => {
          const isExistingUnit = existingRuleUnitIds.has(row.unit_id);
          const hasRuleIds = row.ruleIds && Object.keys(row.ruleIds).length > 0;

          if (isExistingUnit && hasRuleIds) {
            // UPDATE existing rules
            Object.entries(row.ruleIds).forEach(([variantKey, ruleId]) => {
              const variantId = variantKey === '__base__' ? null : variantKey;
              const price = variantId
                ? (Number(row.variant_prices?.[variantId]) || 0)
                : (parseNumberFromSeparator(row.sell_price) || 0);
              const barcode = variantId
                ? (row.variant_barcodes?.[variantId] || "")
                : (row.barcode || "");
              ruleOps.push(
                conversionService.rules
                  .update(ruleId, {
                    conversion_factor: Number(row.conversion_factor),
                    sell_price: price,
                    barcode: barcode,
                  })
                  .catch((err) => {
                    console.warn("Failed to update conversion rule:", err);
                    failedMessages.push(err?.message || "Gagal update aturan konversi");
                    return null;
                  })
              );
            });
          } else if (!isExistingUnit) {
            // CREATE new rules
            const varPriceEntries = Object.entries(row.variant_prices || {});
            if (varPriceEntries.length > 0) {
              varPriceEntries.forEach(([variantId, price]) => {
                ruleOps.push(
                  conversionService.rules
                    .create({
                      product_id: selectedProductId,
                      variant_id: variantId,
                      from_unit_id: row.unit_id,
                      to_unit_id: baseUnitId,
                      conversion_factor: Number(row.conversion_factor),
                      sell_price: price || 0,
                      barcode: row.variant_barcodes?.[variantId] || "",
                    })
                    .catch((err) => {
                      console.warn("Failed to save conversion rule:", err);
                      failedMessages.push(err?.message || "Gagal simpan aturan konversi");
                      return null;
                    })
                );
              });
            } else {
              ruleOps.push(
                conversionService.rules
                  .create({
                    product_id: selectedProductId,
                    from_unit_id: row.unit_id,
                    to_unit_id: baseUnitId,
                    conversion_factor: Number(row.conversion_factor),
                    sell_price: parseNumberFromSeparator(row.sell_price) || 0,
                    barcode: row.barcode || "",
                  })
                  .catch((err) => {
                    console.warn("Failed to save conversion rule:", err);
                    failedMessages.push(err?.message || "Gagal simpan aturan konversi");
                    return null;
                  })
              );
            }
          }
        });

      await Promise.all(ruleOps);

      if (failedMessages.length > 0) {
        showError(failedMessages[0]);
        if (onSuccess) onSuccess();
        onClose();
        return;
      }

      showSuccess("Satuan jual berhasil disimpan");
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Failed to save selling units:", err);
      showError(err.message || "Gagal menyimpan satuan jual");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formatCurrency = (val) =>
    `Rp ${formatNumberWithSeparator(Number(val) || 0)}`;

  const isEditMode = Boolean(editingUnitId);

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh] overflow-hidden modal-panel-enter">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-semibold text-slate-900 tracking-tight">
            {isEditMode ? "Edit Aturan Konversi" : "Tambah Satuan Jual"}
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto bg-slate-50/40">
          {!productId && (
            <div className="mb-5 bg-white ring-1 ring-slate-200/60 rounded-xl p-4 shadow-sm">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                Pilih Produk <span className="text-rose-500">*</span>
              </label>
              <SearchableSelect
                options={[]}
                value={selectedProductId}
                onChange={(value) => {
                  setSelectedProductId(value);
                  setConversionRows([]);
                }}
                fetchOptions={async (term) => {
                  const response = await masterService.products.list({
                    status: "ACTIVE",
                    query: term,
                  });
                  const data = response.data?.items || response.data?.data || response.data || response;
                  return Array.isArray(data) ? data : [];
                }}
                minSearchLength={0}
                placeholder="Cari dan pilih produk..."
                displayKey="name"
                valueKey="id"
              />
            </div>
          )}

          {loadingProductData ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader className="w-8 h-8 animate-spin text-teal-600 mb-2" />
              <p className="text-sm text-slate-500">Memuat data produk...</p>
            </div>
          ) : !selectedProduct && !productId ? (
            <div className="text-center py-12 text-sm text-slate-400 bg-white ring-1 ring-slate-100 rounded-2xl">
              Silakan pilih produk terlebih dahulu untuk mengelola aturan konversi stok.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <span className="w-7 h-7 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-bold">
                    1
                  </span>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Konversi Stok
                  </h3>
                </div>
                {!isEditMode && (
                  <button
                    type="button"
                    onClick={handleAddRow}
                    title="Tambah konversi stok"
                    className="w-7 h-7 rounded-lg ring-1 ring-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100 flex items-center justify-center transition-colors">
                    <Plus className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Info banner */}
              <div className="bg-sky-50/70 ring-1 ring-sky-200 rounded-lg px-3 py-2.5 mb-3 space-y-1.5">
                <p className="text-xs text-slate-700">
                  <span className="text-slate-500">Satuan dasar produk:</span>{" "}
                  <span className="font-semibold">{baseUnitName}</span>
                </p>
                {localVariants.filter(v => v.name).length > 0 ? (
                  <div className="space-y-0.5">
                    <span className="text-[11px] text-slate-500">Harga jual dasar per varian:</span>
                    {localVariants.filter(v => v.name).map(v => (
                      <div key={v.id} className="flex justify-between items-center">
                        <span className="text-xs text-slate-600">{v.name}</span>
                        <span className="text-xs font-semibold text-slate-800">
                          {formatCurrency(v.sell_price)} / {baseUnitName}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-700">
                    <span className="text-slate-500">Harga jual dasar:</span>{" "}
                    <span className="font-semibold">
                      {formatCurrency(hargaJualDasar)} / {baseUnitName}
                    </span>
                  </p>
                )}
              </div>

              {/* Table */}
              <div className="ring-1 ring-slate-200 rounded-xl overflow-hidden bg-white">
                <table className="w-full">
                  <thead className="bg-slate-50/80 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Satuan Jual
                      </th>
                      <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Nilai Konversi
                      </th>
                      <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        Harga Jual
                      </th>
                      <th className="px-4 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-24">
                        Aksi
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {conversionRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-4 py-6 text-center text-sm text-slate-500">
                          Belum ada konversi. Klik{" "}
                          <span className="text-teal-600 font-semibold">+</span>{" "}
                          untuk menambahkan.
                        </td>
                      </tr>
                    ) : (
                      conversionRows.map((row, idx) => {
                        const isExistingRule = existingRuleUnits.has(row.unit_id);
                        return (
                          <tr key={idx}>
                            <td className="px-4 py-2.5 text-sm text-slate-800">
                              {row.unit_name || "-"}
                            </td>
                            <td className="px-4 py-2.5 text-sm text-slate-700">
                              1 {row.unit_name} = {row.conversion_factor}{" "}
                              {baseUnitName}
                            </td>
                            <td className="px-4 py-2.5 text-sm text-slate-900">
                              {row.variant_prices && Object.keys(row.variant_prices).length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {Object.entries(row.variant_prices).map(([variantId, price]) => {
                                    const v = localVariants.find((varItem) => varItem.id === variantId);
                                    return (
                                      <span
                                        key={variantId}
                                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200"
                                      >
                                        <span className="text-slate-500 mr-1 font-normal">{v?.name || "Default"}:</span>
                                        {formatCurrency(price)}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span className="font-semibold text-slate-800">{formatCurrency(row.sell_price)}</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-center">
                                <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => handleEditRow(idx)}
                                  className="w-7 h-7 rounded-lg bg-teal-600 hover:bg-teal-700 text-white flex items-center justify-center transition-colors"
                                  title="Ubah baris">
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                {isExistingRule ? (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    Tersimpan
                                  </span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteRow(idx)}
                                    className="w-7 h-7 rounded-lg bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center transition-colors"
                                    title="Hapus baris">
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {localVariants.length === 0 && (
                <p className="text-[11px] text-amber-700 mt-3 flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Belum ada merek pada produk ini. Konversi stok akan disimpan,
                  namun varian (brand × unit) akan dibuat otomatis ketika Anda
                  menambahkan merek.
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 rounded-b-2xl flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors disabled:opacity-50">
            Batal
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors shadow-sm shadow-teal-600/20 disabled:opacity-60 inline-flex items-center gap-2">
            {loading && <Loader className="w-4 h-4 animate-spin" />}
            {loading ? "Menyimpan..." : "Simpan Satuan Jual"}
          </button>
        </div>
      </div>

      {/* Sub-modal: Tambah Konversi Stok (per baris) */}
      {subModalOpen && (
        <AddConversionRowModal
          baseUnitName={baseUnitName}
          hargaJualDasar={hargaJualDasar}
          activeVariants={localVariants.filter(v => v.name)}
          initialRow={
            editingRowIndex !== null ? conversionRows[editingRowIndex] : null
          }
          onClose={() => {
            setSubModalOpen(false);
            setEditingRowIndex(null);
          }}
          onSave={handleSubModalSave}
        />
      )}
    </div>
  );
}

// =====================================================================
// Sub-modal: Tambah / Ubah Konversi Stok (one row)
// =====================================================================
function AddConversionRowModal({
  baseUnitName,
  hargaJualDasar,
  activeVariants = [],
  initialRow,
  onClose,
  onSave,
}) {
  const [unitId, setUnitId] = useState(initialRow?.unit_id || "");
  const [unitName, setUnitName] = useState(initialRow?.unit_name || "");
  const [conversionFactor, setConversionFactor] = useState(
    initialRow?.conversion_factor || "",
  );
  const [sellPrice, setSellPrice] = useState(
    initialRow?.sell_price ? String(initialRow.sell_price) : "",
  );
  const [variantPrices, setVariantPrices] = useState(initialRow?.variant_prices || {});
  const [barcode, setBarcode] = useState(initialRow?.barcode || "");
  const [variantBarcodes, setVariantBarcodes] = useState(initialRow?.variant_barcodes || {});
  const [error, setError] = useState(null);

  const isEdit = !!initialRow;
  const autoPrice = Math.round(hargaJualDasar * Number(conversionFactor || 0));

  useEffect(() => {
    if (conversionFactor && !initialRow) {
      setSellPrice(autoPrice.toString());
    }
  }, [conversionFactor]);

  const formatCurrency = (val) =>
    `Rp ${formatNumberWithSeparator(Number(val) || 0)}`;

  const handleSave = () => {
    if (!unitId) {
      setError("Pilih satuan jual terlebih dahulu");
      return;
    }
    const factor = Number(conversionFactor);
    if (!factor || factor <= 0) {
      setError("Nilai konversi harus lebih dari 0");
      return;
    }

    const finalPrices = {};
    if (activeVariants.length > 0) {
      activeVariants.forEach((v) => {
        const customVal = variantPrices[v.id];
        const autoVal = Math.round(Number(v.sell_price || 0) * factor);
        finalPrices[v.id] = customVal !== undefined && customVal !== "" ? Number(customVal) : autoVal;
      });
    }

    setError(null);
    onSave({
      unit_id: unitId,
      unit_name: unitName,
      conversion_factor: factor,
      sell_price: sellPrice ? Number(sellPrice) : autoPrice,
      variant_prices: finalPrices,
      barcode: barcode.trim(),
      variant_barcodes: variantBarcodes,
    });
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-2xl w-full max-w-md flex flex-col max-h-[92vh] overflow-hidden modal-panel-enter">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-base font-semibold text-slate-900">
            {isEdit ? "Ubah Konversi Stok" : "Tambah Konversi Stok"}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto">
          {/* Info banner */}
          <div className="bg-sky-50/70 ring-1 ring-sky-200 rounded-lg px-3 py-2.5 space-y-1.5">
            <p className="text-xs text-slate-700">
              <span className="text-slate-500">Satuan dasar:</span>{" "}
              <span className="font-semibold">{baseUnitName}</span>
            </p>
            {activeVariants.length > 0 ? (
              <div className="space-y-0.5">
                <span className="text-[11px] text-slate-500">Harga dasar per varian:</span>
                {activeVariants.map(v => (
                  <div key={v.id} className="flex justify-between items-center">
                    <span className="text-xs text-slate-600">{v.name}</span>
                    <span className="text-xs font-semibold text-slate-800">
                      {formatCurrency(v.sell_price)} / {baseUnitName}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-700">
                <span className="text-slate-500">Harga jual dasar:</span>{" "}
                <span className="font-semibold">
                  {formatCurrency(hargaJualDasar)} / {baseUnitName}
                </span>
              </p>
            )}
          </div>

          {/* Satuan Jual */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Satuan Jual <span className="text-rose-500">*</span>
            </label>
            <SearchableSelect
              options={
                initialRow?.unit_id
                  ? [{ id: initialRow.unit_id, name: initialRow.unit_name }]
                  : []
              }
              value={unitId}
              onChange={(value, opt) => {
                setUnitId(value);
                setUnitName(opt?.name || "");
              }}
              fetchOptions={useCallback(async (term) => {
                const response = await masterService.units.list({
                  query: term,
                  limit: 100,
                });
                const data = response.data?.items || response.data?.data || response.data || response;
                return Array.isArray(data) ? data : [];
              }, [])}
              minSearchLength={0}
              placeholder="Pilih satuan jual"
              displayKey="name"
              valueKey="id"
            />
          </div>

          {/* Nilai Konversi */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Nilai Konversi <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={conversionFactor}
                onChange={(e) =>
                  setConversionFactor(e.target.value.replace(/[^\d.]/g, ""))
                }
                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-3 pr-16 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
                placeholder="Contoh: 33"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 text-xs font-medium">
                {baseUnitName}
              </span>
            </div>
          </div>

          {/* Harga Jual */}
          {activeVariants.length > 0 ? (
            <div className="space-y-3.5 border-t border-slate-100 pt-3">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                Harga Jual Per Varian
              </p>
              {activeVariants.map((v) => {
                const vAutoPrice = Math.round(Number(v.sell_price || 0) * Number(conversionFactor || 0));
                const vPrice = variantPrices[v.id] ?? "";
                return (
                  <div key={v.id}>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">
                      Harga Jual Varian: <span className="font-semibold text-teal-700">{v.name}</span>
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm font-medium">
                        Rp
                      </span>
                      <input
                        type="text"
                        value={formatNumberWithSeparator(vPrice)}
                        onChange={(e) => {
                          const val = parseNumberFromSeparator(e.target.value);
                          setVariantPrices((prev) => ({ ...prev, [v.id]: val }));
                        }}
                        placeholder={formatNumberWithSeparator(vAutoPrice)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
                      />
                    </div>
                    <label className="block text-xs font-medium text-slate-600 mt-2 mb-1.5">
                      Barcode {unitName || "Satuan"} Varian: <span className="font-semibold text-teal-700">{v.name}</span>{" "}
                      <span className="text-slate-400 font-normal">(opsional)</span>
                    </label>
                    <input
                      type="text"
                      value={variantBarcodes[v.id] || ""}
                      onChange={(e) =>
                        setVariantBarcodes((prev) => ({ ...prev, [v.id]: e.target.value.trim() }))
                      }
                      placeholder="Scan / ketik barcode kemasan satuan ini"
                      className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Harga Jual <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 text-sm font-medium">
                  Rp
                </span>
                <input
                  type="text"
                  value={formatNumberWithSeparator(sellPrice)}
                  onChange={(e) => setSellPrice(parseNumberFromSeparator(e.target.value))}
                  placeholder={formatNumberWithSeparator(autoPrice)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-10 pr-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
                />
              </div>
              <label className="block text-xs font-medium text-slate-600 mt-3 mb-1.5">
                Barcode {unitName || "Satuan"} <span className="text-slate-400 font-normal">(opsional)</span>
              </label>
              <input
                type="text"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder="Scan / ketik barcode kemasan satuan ini"
                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
              />
            </div>
          )}

          {/* Info note */}
          <div className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 ring-1 ring-slate-200 rounded-lg px-3 py-2">
            <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
            <span>Nilai konversi mengacu ke satuan dasar produk.</span>
          </div>

          {error && (
            <p className="text-xs text-rose-600 bg-rose-50 ring-1 ring-rose-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 font-medium text-sm transition-colors">
            Batal
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium text-sm transition-colors shadow-sm shadow-teal-600/20">
            Simpan Konversi
          </button>
        </div>
      </div>
    </div>
  );
}
